import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { fmtDate } from '../../shared/Panel';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  fetchStopTimes,
  fetchStopTimesSchedule,
  fetchStopTimesSummary,
  hoursPl,
  KIND_LABEL,
  placeLabel,
  runStopTimes,
  type StopKind,
  type StopScope,
  type StopTimeQuery,
  type StopTimeRow,
  type StopTimeSort,
  type StopTimesSchedule,
  type StopTimesSummary,
} from './stopTimesApi';

/**
 * Zestawienie czasów załadunku i rozładunku według miejsca (08.10.2026,
 * ClickUp 869fd2g52; kryterium akceptacji: „czasy per lokalizacja widoczne
 * w zestawieniu”). Miejsce albo zleceniodawca, rodzaj punktu, mediana
 * z rozrzutem, liczba pomiarów i czy reguły już z niej korzystają.
 *
 * Odczyt dla każdego, kto widzi parametry; „Przelicz teraz” tylko z sekcją
 * administracyjną (to samo `canEdit` co zmiana parametrów — rozstrzyga API).
 */

const PER_PAGE = 25;

/**
 * Opis reguły „tylko miarodajne miejsca” (19.10.2026, przełącznik
 * `stopTimesReliablePlace`; odpowiedź Marathonu 16.10 pyt. 10, decyzja
 * użytkownika „jeśli tylko jest to miarodajne i inne wskaźniki nie mówią
 * o zakończeniu”).
 */
const RELIABLE_NOTE = (s: StopTimesSummary) =>
  `Czas w punkcie to mediana postojów auta przy punktach zrealizowanych zleceń, zmierzona na śladzie GPS. ` +
  `Rekomendacje, zajętość pojazdu i harmonogram biorą medianę miejsca tylko wtedy, gdy jest miarodajna: ` +
  `co najmniej ${s.thresholds.location} postojów, a połowa z nich mieści się w przedziale nie szerszym niż ` +
  `${s.thresholds.maxSpreadPct ?? '…'}% mediany. Inaczej — mediana całej floty (załadunek ${hoursPl(s.defaults.loadingH)}, ` +
  `rozładunek ${hoursPl(s.defaults.unloadingH)}); mediany zleceniodawców nie są używane. Gdy TMS potwierdza, ` +
  `że załadunek albo całe zlecenie jest zrealizowane, czasu postoju po tym odczycie nie doliczamy. ` +
  `Progi są parametrami powyżej. Zestawienie obejmuje pomiary z ostatnich ${s.aggregateDays} dni.`;

const PLACE_NOTE = (s: StopTimesSummary | null) =>
  `Czas w punkcie to mediana postojów auta przy punktach zrealizowanych zleceń, zmierzona na śladzie GPS. ` +
  `Rekomendacje, zajętość pojazdu i harmonogram biorą medianę miejsca od ${s?.thresholds.location ?? '…'} pomiarów, ` +
  `potem zleceniodawcy od ${s?.thresholds.contractor ?? '…'}, a bez danych — wartość domyślną ` +
  `(załadunek ${s ? hoursPl(s.defaults.loadingH) : '…'}, rozładunek ${s ? hoursPl(s.defaults.unloadingH) : '…'}). ` +
  `Zestawienie obejmuje pomiary z ostatnich ${s?.aggregateDays ?? '…'} dni.`;

export const STOP_TIMES_NOTE = (s: StopTimesSummary | null) =>
  s?.rule === 'reliablePlace' ? RELIABLE_NOTE(s) : PLACE_NOTE(s);

/** Kolumny sortowalne — klucz API i nagłówek. */
const SORTABLE: Array<{ key: StopTimeSort; label: string; align?: 'right' }> = [
  { key: 'medianH', label: 'Mediana', align: 'right' },
  { key: 'p75H', label: 'Rozrzut (połowa pomiarów)', align: 'right' },
  { key: 'observations', label: 'Pomiary', align: 'right' },
  { key: 'computedAt', label: 'Przeliczono' },
];

export const StopTimesSection = ({ canEdit }: { canEdit: boolean | null }) => {
  const notify = useNotify();
  const [query, setQuery] = useState<StopTimeQuery>({
    scope: 'location',
    kind: '',
    q: '',
    country: '',
    inUse: false,
    sort: 'observations',
    order: 'desc',
    page: 1,
    perPage: PER_PAGE,
  });
  const [rows, setRows] = useState<StopTimeRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<StopTimesSummary | null>(null);
  const [schedule, setSchedule] = useState<StopTimesSchedule | null>(null);
  const [busy, setBusy] = useState(false);

  const loadMeta = useCallback(async () => {
    // Nagłówek i stan harmonogramu — błąd nie zasłania tabeli.
    const [s, h] = await Promise.allSettled([fetchStopTimesSummary(), fetchStopTimesSchedule()]);
    if (s.status === 'fulfilled') setSummary(s.value);
    if (h.status === 'fulfilled') setSchedule(h.value);
  }, []);

  const load = useCallback(async (q: StopTimeQuery) => {
    try {
      const result = await fetchStopTimes(q);
      setRows(result.rows);
      setTotal(result.total);
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, []);

  useEffect(() => {
    void loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    // Wpisywanie w wyszukiwarkę nie pyta API po każdej literze.
    const timer = setTimeout(() => void load(query), 300);
    return () => clearTimeout(timer);
  }, [load, query]);

  const change = (patch: Partial<StopTimeQuery>) => setQuery((q) => ({ ...q, page: 1, ...patch }));

  const sortBy = (key: StopTimeSort) =>
    setQuery((q) => ({
      ...q,
      page: 1,
      sort: key,
      order: q.sort === key && q.order === 'desc' ? 'asc' : 'desc',
    }));

  const run = async () => {
    setBusy(true);
    try {
      const r = await runStopTimes();
      notify(`Przeliczono czasy postoju: zmierzono ${r.examined} punktów, przyjęto ${r.accepted}`, {
        type: 'success',
      });
      await Promise.all([load(query), loadMeta()]);
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const lastRun = summary?.runs[0] ?? null;
  const scopeLabel = query.scope === 'contractor' ? 'Zleceniodawca' : 'Miejsce';

  return (
    <Box sx={{ mt: 4 }} data-testid="stop-times">
      <Typography variant="h6" sx={{ mb: 1 }}>
        Czasy załadunku i rozładunku według miejsca
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }} data-testid="stop-times-note">
        {STOP_TIMES_NOTE(summary)}
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { md: 'center' } }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} data-testid="stop-times-status">
          {summary && summary.observations.length > 0
            ? `Pomiary: ${summary.observations.map((o) => `${o.label} ${o.count}`).join(', ')}. `
            : 'Pomiarów jeszcze nie ma — działają wartości domyślne. '}
          {lastRun
            ? `Ostatnie przeliczenie: ${fmtDate(lastRun.startedAt)}${lastRun.error ? ` — błąd: ${lastRun.error}` : ''}${lastRun.note ? ` — ${lastRun.note}` : ''}. `
            : ''}
          {schedule
            ? schedule.enabled
              ? `Przeliczenie co noc o ${String(schedule.hour).padStart(2, '0')}:00.`
              : 'Przeliczenie nocne wyłączone (CZASY_POSTOJU_PRZELICZANIE).'
            : ''}
        </Typography>
        {canEdit === true && (
          <Button variant="outlined" size="small" onClick={run} disabled={busy || schedule?.running === true}>
            {busy ? 'Przeliczanie…' : 'Przelicz teraz'}
          </Button>
        )}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={query.scope}
          onChange={(_e, v: StopScope | null) => v && change({ scope: v })}
          aria-label="Zakres zestawienia"
        >
          <ToggleButton value="location">Miejsca</ToggleButton>
          <ToggleButton value="contractor">Zleceniodawcy</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          select
          size="small"
          label="Rodzaj"
          value={query.kind ?? ''}
          onChange={(e) => change({ kind: e.target.value as StopKind | '' })}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Wszystkie</MenuItem>
          <MenuItem value="loading">Załadunek</MenuItem>
          <MenuItem value="unloading">Rozładunek</MenuItem>
        </TextField>
        <TextField
          size="small"
          label={query.scope === 'contractor' ? 'Nazwa zleceniodawcy' : 'Miasto albo kod pocztowy'}
          value={query.q ?? ''}
          onChange={(e) => change({ q: e.target.value })}
        />
        {query.scope === 'location' && (
          <TextField
            size="small"
            label="Kraj (np. PL)"
            value={query.country ?? ''}
            onChange={(e) => change({ country: e.target.value })}
            sx={{ width: 120 }}
            slotProps={{ htmlInput: { maxLength: 2 } }}
          />
        )}
        <FormControlLabel
          control={<Checkbox checked={query.inUse === true} onChange={(e) => change({ inUse: e.target.checked })} />}
          label="Tylko używane w rekomendacjach"
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Nie udało się wczytać zestawienia: {error}
        </Alert>
      )}
      {rows === null && !error ? (
        <CircularProgress size={24} />
      ) : rows !== null && rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="stop-times-empty">
          Brak wierszy — przy tych filtrach albo przed pierwszym przeliczeniem. Działają wartości domyślne.
        </Typography>
      ) : rows !== null ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Czasy postoju">
            <TableHead>
              <TableRow>
                <TableCell>{scopeLabel}</TableCell>
                <TableCell>Rodzaj</TableCell>
                {SORTABLE.map((c) => (
                  <TableCell key={c.key} align={c.align} sortDirection={query.sort === c.key ? query.order : false}>
                    <TableSortLabel
                      active={query.sort === c.key}
                      direction={query.sort === c.key ? query.order : 'desc'}
                      onClick={() => sortBy(c.key)}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{placeLabel(r)}</TableCell>
                  <TableCell>{KIND_LABEL[r.kind] ?? r.kind}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {hoursPl(r.medianH)}
                  </TableCell>
                  <TableCell align="right">
                    {hoursPl(r.p25H)} – {hoursPl(r.p75H)}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                      <span>{r.observations}</span>
                      {r.inUse ? (
                        <Chip size="small" color="success" variant="outlined" label="w użyciu" />
                      ) : r.notUsedReason ? (
                        // Reguła miarodajnych miejsc (19.10.2026): krótki znacznik,
                        // powód w dymku — tabela mieści się przy 1366 px.
                        <Tooltip title={r.notUsedReason}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={r.scope === 'contractor' ? 'nieużywana' : 'niemiarodajna'}
                          />
                        </Tooltip>
                      ) : (
                        <Chip size="small" variant="outlined" label={`za mało (próg ${r.threshold})`} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.computedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={query.page - 1}
            onPageChange={(_e, p) => setQuery((q) => ({ ...q, page: p + 1 }))}
            rowsPerPage={PER_PAGE}
            rowsPerPageOptions={[PER_PAGE]}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
          />
        </Box>
      ) : null}
    </Box>
  );
};
